import { Button, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import useUserState from '../../../../../hooks/useUserState';
import { TranslationsKeys } from '../../../../../locales/translations';
import { PermissionName } from '../../../../../types/Auth';

import { getInstallmentSettings, saveInstallmentSettings } from './api';
import { InstallmentSettingsContext } from './context';
import {
  DEFAULT_VALUES,
  hasError,
  parseFromFetch,
  parseToFetch,
} from './parse-values';
import InterestsRatesSection from './sections/InterestsRatesSection';
import QuotaRoundingSection from './sections/QuotaRoundingSection';
import TariffDownPaymentSection from './sections/TariffDownPaymentSection';
import { InstallmentSettingsForm } from './types';

/**
 * Intereses corrientes, cuotas y abonos. Los textos siguen leyéndose del
 * namespace de Configuración > Financiero Matrícula, de donde vienen estas
 * secciones: renombrar las claves rompería los overrides por tenant que ya
 * apuntan ahí.
 */
export default function InstallmentsTab() {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_FINANTIAL);
  const { isAuthorizedTo } = useUserState();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<InstallmentSettingsForm>({
    defaultValues: DEFAULT_VALUES,
  });
  const { reset, handleSubmit } = methods;

  const canEdit = useMemo(
    () => !!isAuthorizedTo([PermissionName.FINANCIAL_PORTAL_SETTINGS]),
    [isAuthorizedTo],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getInstallmentSettings();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`toasts.errorSavingChanges`),
        });
      } else {
        reset(parseFromFetch(data));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reset, t]);

  const handleSave = useCallback(
    async (values: InstallmentSettingsForm) => {
      if (isSaving) {
        return;
      }
      const payload = parseToFetch(values);
      const validationError = hasError(payload);
      if (validationError) {
        addToast({ icon: 'error', color: 'danger', text: t(validationError) });
        return;
      }

      setIsSaving(true);
      const { error } = await saveInstallmentSettings(payload);
      setIsSaving(false);

      addToast(
        error
          ? {
              icon: 'error',
              color: 'danger',
              text: t(`toasts.errorSavingChanges`),
            }
          : {
              icon: 'success',
              color: 'success',
              text: t(`toasts.changesSaved`),
            },
      );
    },
    [isSaving, t],
  );

  if (isLoading) {
    return (
      <Card className="p-4">
        <Loading insideCard />
      </Card>
    );
  }

  return (
    <InstallmentSettingsContext.Provider
      value={{ loading: isLoading, disabled: isSaving || !canEdit }}
    >
      <FormProvider {...methods}>
        <form className="w-100" onSubmit={handleSubmit(handleSave)}>
          <Card className="p-4">
            <Row>
              <Col sm={12}>
                <InterestsRatesSection />
              </Col>
              <Col sm={12}>
                <QuotaRoundingSection />
              </Col>
              <Col sm={12}>
                <TariffDownPaymentSection />
              </Col>
              <Col sm={12}>
                {canEdit && (
                  <Button
                    type="submit"
                    text={t(`actions.save`)}
                    size="md"
                    className="float-right mt-5"
                    disabled={isSaving}
                    loading={isSaving}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </form>
      </FormProvider>
    </InstallmentSettingsContext.Provider>
  );
}
